<script setup lang="ts">
import { computed, ref } from 'vue';
import type { ExpertProfileStatus, ExpertTypeBucket, ListingType } from '@shopexperts/legacy-api';
import ExpertStatsSegmentedBar, { type ExpertStatsSegment } from './ExpertStatsSegmentedBar.vue';

const props = defineProps<{
  label: string;
  icon: string;
  bucket: ExpertTypeBucket;
  accentClass: string;
  typeKey: ListingType;
  activeStatus?: string;
  activeExpertType?: string;
}>();

const emit = defineEmits<{
  applyFilters: [filters: { status: ExpertProfileStatus; expertType: ListingType }];
}>();

const hoveredStatus = ref<ExpertProfileStatus | null>(null);

const statusLegend = [
  { key: 'published', label: 'Published', color: 'bg-emerald-500', textClass: 'text-emerald-700' },
  { key: 'application', label: 'Application', color: 'bg-amber-400', textClass: 'text-amber-700' },
  { key: 'draft', label: 'Draft', color: 'bg-slate-300', textClass: 'text-gray-500' },
  { key: 'unpublished', label: 'Unpublished', color: 'bg-rose-400', textClass: 'text-rose-600' },
  { key: 'banned', label: 'Banned', color: 'bg-gray-700', textClass: 'text-gray-700' },
] as const;

const segments = computed<ExpertStatsSegment[]>(() => {
  const { statuses } = props.bucket;
  const total = props.bucket.total || 1;
  return statusLegend
    .map((status) => ({
      ...status,
      count: statuses[status.key],
      pct: (statuses[status.key] / total) * 100,
      colorClass: status.color,
    }))
    .filter(segment => segment.count > 0)
    .map(({ color, ...segment }) => segment);
});

const activeSegmentKey = computed(() => (
  props.activeExpertType === props.typeKey ? props.activeStatus : undefined
));

const isCardActive = computed(() => (
  hoveredStatus.value !== null ||
  (props.activeStatus === activeSegmentKey.value && !!activeSegmentKey.value)
));

function setHoveredStatus(status: ExpertProfileStatus | null) {
  hoveredStatus.value = status;
}

function applyFilters(status: ExpertProfileStatus) {
  emit('applyFilters', { status, expertType: props.typeKey });
}
</script>

<template>
  <div class="stats-type-card group" :class="{ 'is-active': isCardActive }">
    <div class="flex items-center justify-between mb-3">
      <div class="flex items-center gap-2">
        <span class="type-icon" :class="accentClass">{{ icon }}</span>
        <span class="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400">{{ label }}</span>
      </div>
      <span class="type-total" :class="{ 'is-active': isCardActive }">{{ bucket.total }}</span>
    </div>

    <!-- Mini proportion bar -->
    <ExpertStatsSegmentedBar
      v-if="bucket.total > 0"
      class="mt-0"
      :segments="segments"
      :active-key="activeSegmentKey"
      :min-segment-pct="2"
      item-variant="chip"
      :aria-label-prefix="`Filter ${label} by`"
      @hover-change="setHoveredStatus"
      @select="applyFilters"
    />
    <div v-else class="h-1.5 w-full rounded-full bg-gray-100" />
  </div>
</template>

<style scoped>
.stats-type-card {
  padding: 14px 16px;
  border-radius: 10px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.stats-type-card:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  border-color: rgba(0, 0, 0, 0.08);
}

.stats-type-card.is-active {
  border-color: rgba(15, 23, 42, 0.08);
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
}

.type-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1;
}

.type-total {
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  color: #111827;
  font-variant-numeric: tabular-nums;
  transition: color 0.18s ease;
}

.type-total.is-active {
  color: #0f172a;
}
</style>
